import { Subscription } from 'rxjs';
import {
    createSseConnection,
    SseConnection,
} from './sseConnection';
import { TimestampedSseMessage } from '../../types/TimestampedSseMessage';

export function getBetBuilderSseConnectionRegistry<T>(url: string) {
    const connections = new Map<string, ConnectionMetadata<T>>();

    const subscribers: Array<(message: SubscriptionMessage<T>) => void> = [];

    function subscribe(subscriber: (message: SubscriptionMessage<T>) => void) {
        subscribers.push(subscriber);
    }

    function unsubscribe(subscriber: (message: SubscriptionMessage<T>) => void) {
        const index = subscribers.indexOf(subscriber);
        if (index !== -1) {
            subscribers.splice(index, 1);
        }
    }

    function establishConnectionForKey(
        key: string,
        queryParams: Record<string, string>,
        headers: Record<string, string> = {},
    ) {
        if (connections.get(key)) {
            disconnectForKey(key);
        }

        const fullUrl = `${url}?${new URLSearchParams(queryParams).toString()}`;

        const connection = createSseConnection<TimestampedSseMessage<T>>(fullUrl, headers);

        const subscription = connection.observable.subscribe((message) => {
            if (message) {
                subscribers.forEach((subscriber) => {
                    if (message?.data) {
                        subscriber({ key, data: message.data });
                    }
                });
            }
        });

        connections.set(key, {
            connection,
            subscription,
        });
    }

    function disconnectForKey(key: string) {
        connections.get(key)?.connection?.unsubscribe();
        connections.get(key)?.subscription?.unsubscribe();
        connections.delete(key);
    }

    function disconnectAll() {
        connections.forEach(({ connection, subscription }) => {
            connection.unsubscribe();
            subscription.unsubscribe();
        });
        connections.clear();
    }

    return {
        subscribe,
        unsubscribe,
        establishConnectionForKey,
        disconnectForKey,
        disconnectAll,
    };
}

type SubscriptionMessage<T> = {
    key: string;
    data: T;
};

type ConnectionMetadata<T> = {
    connection: SseConnection<TimestampedSseMessage<T>>;
    subscription: Subscription;
};
